// Host "kill the ad overlay" CSS erases anything portaled into document.body: `body >
// div:not([class])` matches an unclassed body child, `[style*="2147483"]` a max-int z-index.
// The second matches the style ATTRIBUTE, so declaring z-index here keeps max-int stacking.

import { injectStyleSheet } from './injectStyleSheet';

/** One selector for every malachite overlay, so a host allow-lists them in one rule. */
export const OVERLAY_ATTR = 'data-malachite-overlay';

// Values avoid `panel`/`popup`/`overlay`/`tooltip`: a class defeats `:not([class])`, but one
// matching `[class*="panel"]` trades that hole for another these elements did not have.
export const OVERLAY_CLASS = {
    dropdown: 'malachite-dropdown-surface',
    multiselect: 'malachite-multiselect-surface',
    actionmenu: 'malachite-actionmenu-surface',
    tooltip: 'malachite-tip-surface',
    sidepanel: 'malachite-drawer-surface',
    modal: 'malachite-modal-surface',
} as const;

export type OverlayName = keyof typeof OVERLAY_CLASS;

export const overlayIdentity = (name: OverlayName) => ({
    className: OVERLAY_CLASS[name],
    [OVERLAY_ATTR]: name,
});

const OVERLAY_STYLE_ID = 'malachite-overlay-styles';

/**
 * Where SidePanel and Modal start. `useOverlayBehaviour` steps one per stacked overlay, so
 * paint order follows the focus stack rather than body order — a handful above this at most.
 */
export const OVERLAY_Z_BASE = 1000;

/**
 * The dropdown band, which clears app chrome pinned just under max-int. One integer wide: a host
 * pins its chrome at max-int-1 and the tooltip band sits at max-int, so the stack cannot step
 * per layer the way the base band does — the top layer orders it, and where popover is
 * unsupported, document order does.
 */
export const OVERLAY_Z_ELEVATED = 2147483646;

/** Marks a host in that band — an attribute, not an inline z-index; see above. */
export const OVERLAY_ELEVATED_ATTR = 'data-malachite-elevated';

// Every overlay class, not the four promoted by default: one the reset misses wears the UA box.
const promotedSelector = (suffix: string) =>
    Object.values(OVERLAY_CLASS)
        .map((name) => `.${name}:popover-open${suffix}`)
        .join(', ');

// Promotion drags in the UA `[popover]` box: `inset: 0` (which survives as right/bottom and
// stretches a panel to the viewport edge), auto margins, a border, padding and fit-content
// sizing. Reset to what these computed before promotion; their own inline styles still win.
const POPOVER_RESET =
    'inset: auto; margin: 0; border: 0; padding: 0; width: auto; height: auto; ' +
    'overflow: visible; background: none; color: inherit;';

const OVERLAY_RULES = [
    `.${OVERLAY_CLASS.dropdown}, .${OVERLAY_CLASS.multiselect}, .${OVERLAY_CLASS.actionmenu} { z-index: 2147483646; }`,
    `.${OVERLAY_CLASS.tooltip} { z-index: 2147483647; }`,
];

// Its own id, never folded into OVERLAY_STYLE_ID: injectStyleSheet keeps one sheet per id across
// malachite copies sharing a realm, so a copy predating this rule would win that id and leave a
// portaled menu on the host page's box model — a 280px Options menu painting 298. No copy has
// ever registered this one.
const MENU_BOX_STYLE_ID = 'malachite-overlay-menu-box';

// The scoped stylesheet makes everything border-box; a portal escapes it onto a host page that
// may have no reset at all, while the geometry is computed as a CSS width.
const MENU_BOX_RULES = [
    `.${OVERLAY_CLASS.dropdown}, .${OVERLAY_CLASS.dropdown} *,` +
        ` .${OVERLAY_CLASS.multiselect}, .${OVERLAY_CLASS.multiselect} *,` +
        ` .${OVERLAY_CLASS.actionmenu}, .${OVERLAY_CLASS.actionmenu} *` +
        ' { box-sizing: border-box; }',
];

export const ensureOverlayStyles = (): CSSStyleSheet | null => {
    injectStyleSheet(MENU_BOX_STYLE_ID, MENU_BOX_RULES);
    return injectStyleSheet(OVERLAY_STYLE_ID, OVERLAY_RULES);
};

// Its own id like TOP_LAYER_STYLE_ID: a copy predating elevation would own OVERLAY_STYLE_ID.
const ELEVATED_STYLE_ID = 'malachite-overlay-elevated';

const ELEVATED_RULES = [
    `.${OVERLAY_CLASS.sidepanel}[${OVERLAY_ELEVATED_ATTR}], .${OVERLAY_CLASS.modal}[${OVERLAY_ELEVATED_ATTR}] { z-index: ${OVERLAY_Z_ELEVATED}; }`,
];

export const ensureElevatedOverlayStyles = (): CSSStyleSheet | null =>
    injectStyleSheet(ELEVATED_STYLE_ID, ELEVATED_RULES);

// Its own id, never folded into OVERLAY_STYLE_ID: injectStyleSheet keeps one sheet per id across
// malachite copies sharing a realm, so a copy predating the reset would win that id and leave a
// promoted overlay wearing the raw UA box. No copy has ever registered this one.
const TOP_LAYER_STYLE_ID = 'malachite-overlay-top-layer';

const TOP_LAYER_RULES = [
    `${promotedSelector('')} { ${POPOVER_RESET} }`,
    `${promotedSelector('::backdrop')} { display: none; }`,
];

export const ensureTopLayerStyles = (): CSSStyleSheet | null => {
    // A sheet that installed does not mean its rules parsed: replaceSync (and the <style> path)
    // drop an unparseable rule silently rather than throwing, so an engine that chokes on
    // `:popover-open` or `::backdrop` hands back a valid, empty sheet.
    const sheet = injectStyleSheet(TOP_LAYER_STYLE_ID, TOP_LAYER_RULES);
    return sheet && sheet.cssRules.length > 0 ? sheet : null;
};
